import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { editGallery, getGallery } from "../store/slices/gallery";
import { nameregex } from "../helper/regex";
import { API_BASE_URL, API_PATHS } from "../utils/constants/api.constants";
import { GrUploadOption } from "react-icons/gr";

const EditGalleryImage = () => {
  const { id } = useParams();
  let user = JSON.parse(localStorage.getItem("users"));
  const token = user.data.accessToken;
  const [title, setTitle] = useState("");
  const [image, setImage] = useState("");
  const [imageError, setImageError] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchGalleryDetails = async () => {
      try {
        let item = { id };
        const data = await dispatch(getGallery(item)).unwrap();
        const gallery = data?.data[0];

        if (gallery) {
          setTitle(gallery.title || "");
          setImage(gallery.image || "");
        }
      } catch (error) {
        console.error("Failed to fetch gallery details:", error.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchGalleryDetails();
  }, [dispatch, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const validationErrors = { ...errors };

    switch (name) {
      case "title":
        setTitle(value);
        validationErrors[name] =
          value.trim() === "" || !nameregex.test(value)
            ? "Please enter a valid title"
            : "";
        break;

      default:
        break;
    }

    setErrors(validationErrors);
  };

  const EditGal = async (e) => {
    e.preventDefault();
    setIsDisabled(true);
    setTimeout(() => setIsDisabled(false), 3000);

    const validationErrors = {};

    // Validate name
    if (!title.trim()) {
      validationErrors.name = "Please enter title";
    } else if (!nameregex.test(title)) {
      validationErrors.name = "Title must be at least three characters";
    }

    // Validate iconUrl
    if (!image) {
      validationErrors.image = "Please upload an image";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const item = {
      title,
      image,
      id,
    };

    try {
      await dispatch(editGallery(item)).unwrap();
      alert("Gallery details edited successfully!");
      navigate("/admin/gallery");
    } catch (error) {
      alert(error);
    }
  };

  function cancel(e) {
    navigate(-1);
  }

  async function imageUpload(e) {
    setImageError("");
    const file = e.target.files[0];

    if (!file) {
      setImageError("Please select a file to upload");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setImageError("Please upload an image less than 5 MB");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    const requestOptions = {
      method: "POST",
      body: formData,
      headers: {
        "x-access-token": token,
      },
    };

    try {
      const response = await fetch(
        `${API_BASE_URL}${API_PATHS.upload}`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }

      const data = await response.json();

      if (data?.status === 1 && data?.data?.location) {
        const uploadedIconUrl = data.data.location;
        setImage(uploadedIconUrl);
        setErrors((prevErrors) => ({ ...prevErrors, image: "" }));
      } else {
        throw new Error(data.message || "Failed to upload image");
      }
    } catch (error) {
      console.error("Image Upload Error:", error.message);
      setImageError(
        error.message || "Something went wrong while uploading the image"
      );
    }
  }

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <h6 className="fw-semibold mb-0">Edit Gallery Image</h6>
        <ul className="d-flex align-items-center gap-2">
          <li className="fw-medium">
            <Link
              to="/admin/gallery"
              className="d-flex align-items-center gap-1 hover-text-primary"
            >
              Gallery
            </Link>
          </li>
          <li> &gt; </li>
          <li className="fw-medium">Edit Gallery Image</li>
        </ul>
      </div>
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <form onSubmit={EditGal}>
            <div className="row mb-40">
              <div className="col-md-5">
                <label className="form-label">
                  Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={title}
                  onChange={handleChange}
                  className={`form-control ${errors.title ? "is-invalid" : ""}`}
                  placeholder="Enter Tiitle"
                />
                {errors.title && (
                  <div className="text-danger mt-2">{errors.title}</div>
                )}
              </div>

              <div className="col-md-5">
                <label className="form-label">
                  Image<span className="text-danger">*</span>
                </label>
                <br />
                <label htmlFor="fileInput" className="custom-button">
                  <GrUploadOption
                    style={{ fontSize: "16px", lineHeight: "18px" }}
                  />
                  &nbsp; Upload Image
                </label>
                <input
                  type="file"
                  name="iconUrl"
                  id="fileInput"
                  accept="image/*"
                  onChange={imageUpload}
                  className={`form-control ${imageError ? "is-invalid" : ""}`}
                />
                {errors.image && (
                  <div className="text-danger mt-2">{errors.image}</div>
                )}
                <br />
                <img
                  className="mt-3"
                  src={image}
                  style={{ height: "100px" }}
                  alt=""
                />
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-center gap-3">
              <button
                type="button"
                onClick={cancel}
                className="btn btn-outline-primary-600 wd-100 radius-8 px-14 py-6 text-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isDisabled}
                className="btn btn-primary-600 wd-100 radius-8 px-14 py-6 text-md"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditGalleryImage;
